import { createContext, useContext, ReactNode, useEffect } from "react";
import { FeatureFlags } from "../types";

type FeatureFlagsContextProps = {
  flags: FeatureFlags;
  experiencesRawString?: string;
};

export const createFeatureFlagsContext = () => {
  const Context = createContext<FeatureFlagsContextProps | undefined>(
    undefined,
  );

  const FeatureFlagsProvider = ({
    children,
    flags,
    experiencesRawString,
    id,
  }: {
    children: ReactNode;
    flags: FeatureFlags;
    experiencesRawString?: string;
    id: string;
  }) => {
    useEffect(() => {
      window.featureFlags = window.featureFlags || {};
      window.featureFlags[id] = flags;
    }, [id, flags]);

    useEffect(() => {
      window.listFlags = (inputId) => {
        if (window.featureFlags[inputId]) {
          // eslint-disable-next-line no-console
          console.table(window.featureFlags[inputId]);
        }
        if (
          !inputId ||
          Object.keys(window.featureFlags).indexOf(inputId) === -1
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            `listFlags called without correct context id. Please provide one of "${Object.keys(window.featureFlags).join(", ")}" to see the current flag settings for that context.`,
          );
        }
      };
    }, []);

    return (
      <Context.Provider value={{ flags, experiencesRawString }}>
        {children}
      </Context.Provider>
    );
  };

  const useFeatureFlags = (): FeatureFlags => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        "useFeatureFlags must be used within a FeatureFlagsProvider",
      );
    }
    return context.flags;
  };

  const useExperienceString = () => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        "useExperienceString must be used within a FeatureFlagsProvider",
      );
    }
    return context.experiencesRawString;
  };

  return {
    Provider: FeatureFlagsProvider,
    useFeatureFlags,
    useExperienceString,
  };
};
